import { useState, useCallback, useEffect } from "react";

import * as Sentry from "@sentry/nextjs";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ChatBotApi } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { useGetImageUrl } from "@/components/tiptap/context/ImageUrlContext";
import { nodeFromJson, isNodeJson } from "@/models/nodes/nodeFromJson";
import { useStore } from "@/store";
import { trackEvent } from "@/utils/trackEvent";
import { trackNodesCreated } from "@/utils/trackNodeCreation";
import { twMerge } from "@/utils/twMerge";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

type Props = {
  src: string;
  imageKey: string;
  scope: UploadScope | undefined;
  editorScope: UploadScope | undefined;
  getPos: () => number | undefined;
  doc: DocumentModel;
  node: ProsemirrorNode;
  imageSelected: boolean;
  imageWidth: number;
  /** Renders as a translucent dark chip in normal flow, for a parent that positions it over the image. */
  overlay?: boolean;
};

const WIDTH_THRESHOLD = 150;

export const ImageOcr = observer(function ImageOcr(props: Props) {
  if (!props.doc.editable) {
    return null;
  }

  return <ImageOcrButton {...props} />;
});

function ImageOcrButton({
  src,
  imageKey,
  scope,
  editorScope,
  node,
  getPos,
  doc,
  imageSelected,
  imageWidth,
  overlay,
}: Props) {
  const [ocrStatus, setOcrStatus] = useState<"idle" | "loading" | "done" | "error">("idle");

  const t = useTranslations();
  const { auth, dialog } = useStore();
  const getImageUrl = useGetImageUrl();

  const { editor } = doc;

  const handleOcr = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      trackEvent("ocr_clicked", { source: "image_button", doc_id: doc.id });

      if (!auth.isLoggedIn) {
        dialog.loginDialog.open();
        return;
      }

      if (src) {
        setOcrStatus("loading");
        try {
          const url = (imageKey && scope && (await getImageUrl(scope, imageKey, true))) || src;
          const source: ChatBotApi.OcrSource | undefined = editorScope && {
            type: editorScope.type === "chat" ? "Chat" : "Document",
            uid: editorScope.uid,
          };
          const data = await ChatBotApi.ocr(url, source);

          if (typeof data === "object" && "error" in data) {
            setOcrStatus("error");
          } else if (
            isNodeJson(data, { ignoreId: true }) &&
            data.type === "text" &&
            typeof data.props.value === "string"
          ) {
            setOcrStatus("done");

            // TODO: handle errors
            doc.transact(() => {
              const nodes = data.children
                .map((nodeJson) => nodeFromJson(doc, nodeJson))
                .filter(Boolean);
              doc.textNode.appendChildren(nodes);

              trackEvent("nodes_created_from_ocr", { doc_id: doc.id });
              trackNodesCreated(data.children, "ocr");
            }, "ocr");

            const pos = getPos();
            if (pos === undefined) {
              return;
            }
            const targetPos = pos + node.nodeSize;
            editor.tiptap
              ?.chain()
              .focus()
              .insertContentAndHighlight(data.props.value, targetPos)
              .run();
            doc.editor.suggestions.reset();
          } else {
            setOcrStatus("error");
          }
        } catch (error) {
          setOcrStatus("error");
          Sentry.captureException(error);
        }
      }
    },
    [src, imageKey, scope, editorScope, getImageUrl, doc, editor, getPos, node, auth, dialog],
  );

  const hideUnhovered = ocrStatus !== "loading" && ocrStatus !== "error" && !imageSelected;

  /* eslint-disable no-nested-ternary */
  return (
    <button
      type="button"
      onClick={handleOcr}
      disabled={ocrStatus === "loading"}
      className={twMerge(
        "flex flex-nowrap items-center rounded-8",
        overlay
          ? [
              "p-[5px] gap-1 bg-black/50 text-white text-size-14 font-medium tracking-[-0.02em]",
              "[--color-icon-main:var(--color-white)]",
            ]
          : [
              "text-xs tracking-tight",
              "absolute z-10 bottom-[10px]",
              imageWidth > WIDTH_THRESHOLD ? "right-[13px]" : "-right-[13px] translate-x-full",
              "py-[5px] pr-[10px] pl-[5px]",
              "bg-block-bg shadow-block dark:shadow-block-inverted",
            ],
        hideUnhovered && "hidden group-hover:flex active:translate-y-[1px]",
        "group/button",
        ocrStatus === "error" && "text-rose-500",
      )}
    >
      {ocrStatus === "loading" ? (
        <>
          <span className="inline-block h-[24px]">
            <LoadingIcon className="inline-block mr-[4px]" />
          </span>
          {t("doc.image.recognizeLoading")}
        </>
      ) : ocrStatus === "error" ? (
        <>
          <Icon name="RecognizeError" className="mr-[4px] inline-block" />
          {t("doc.image.recognizeError")}
        </>
      ) : (
        <>
          <Icon name="Recognize" className="mr-[4px] inline-block group-hover/button:hidden" />
          <Icon name="RecognizeHover" className="mr-[4px] hidden group-hover/button:inline-block" />
          {t("doc.image.recognize")}
        </>
      )}
    </button>
  );
  /* eslint-enable no-nested-ternary */
}

function LoadingIcon({ className }: { className?: string }) {
  const PROGRESS_INTERVAL = 3_000;

  type Steps = 0 | 1 | 2 | 3 | 4;

  const [step, setStep] = useState<Steps>(0);

  useEffect(() => {
    if (step >= 4) return;

    const interval = setInterval(() => {
      setStep((prev) => {
        if (prev >= 4) {
          clearInterval(interval);
          return 4;
        }
        return (prev + 1) as Steps;
      });
    }, PROGRESS_INTERVAL);

    return () => clearInterval(interval);
  }, [step]);

  return <Icon name={`RecognizeLoading${step}`} className={className} />;
}
